import _ from "lodash";
/** external */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Modal, CardSelection } from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import ClassNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import update from "immutability-helper";
/** internal */
import UILabel from "UIComponents/UILabel";
import { getPrintFile } from "modules/PrintThunks";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  getUserId,
  getUserType,
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { getSettingValue } from "modules/PermissionModule";
import {
  TEXT_RESPONSE,
  ASSESSMENT_TYPE_WORKSHEET,
  FIELD_UID_VOICE_INSTRUCTIONS,
  RESOURCES,
  AUDIO_INSTRUCTIONS,
  FINAL_REMARKS,
  OVERALL_ASSESSMENT_TOOL,
  ASSESSMENT_CRITERIA,
} from "Worksheet/modules/WorksheetConstants";
import { SCORE, COMMENT } from "Constants/stringConstants";
import {
  CURRICULUM_LEVEL,
  UPDATE_USER_CURRICULUM_SETTINGS,
} from "Constants/permissionConstants";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getAssessmentQuestionsQueryVariables } from "Worksheet/modules/WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { getPlannerOrganizationResourcesOfNodeFromCache } from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import { getPlannerOrganizationResourcesOfNodeVariables } from "UnitPlans/modules/Utils";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import {
  getAssessmentQuestionsEnhancer,
  getAssessmentQuestionsEnhancerV2,
} from "Worksheet/modules/WorksheetEnhancers";
import FullScreenLoader from "UIComponents/FullScreenLoader";
/** relative */
import classes from "./DownloadWorksheetModal.scss";
import {
  getInstructionOptions,
  getEvaluationToolOptions,
  questionLayoutDefaultOptions,
  getOtherItemOptions,
  getUpdatedModalFieldsSettings,
  getHasApplicableSettings,
  getTruthyKeysFromObject,
  arrayKeysToTruthyObject,
  getHasLearningGoals,
  getSettingsInfoFromData,
} from "./DownloadWorksheetModalUtils";
import ToggleSwitchList from "../Question/QuestionFieldComponent/ToggleSwitchList";
import QuestionFieldLabel from "../Question/QuestionFieldLabel";

const styles = {
  checkListContainer: {
    flexWrap: "unset",
    flexDirection: "column",
    gap: "16px",
  },
  listItemStyle: {
    margin: "2px 0px",
    flexBasis: "unset",
  },
  labelContainerStyle: {
    marginBottom: "12px",
  },
  containerStyle: {
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "space-between",
    width: "288px",
    overflow: "hidden",
  },
};

const DownloadWorksheetModal = props => {
  const {
    t,
    toggleDownloadWorksheetModal,
    selectedWorksheetDownloadModalSetting,
    curriculumProgramId,
    updateSettings,
    getPrintFile,
    parentId,
    assignmentId,
    portalType,
    courseId,
    // Base settings from mapStateToProps
    baseInstructionSettings,
    baseEvaluationToolSettings,
    // Data for computation
    curriculumProgramType,
    learningGoalsFieldUid,
    isQueriesV2Enabled,
    // Props from enhancers
    data,
    isLoading,
  } = props;

  const [isPDFDownloading, setIsPDFDownloading] = useState(false);

  // Get computed settings using utility function
  const {
    hasTextResponseBasedQuestion,
    answerBookletSettings,
    instructionSettings,
    evaluationToolSettings,
  } = useMemo(
    () =>
      getSettingsInfoFromData({
        data,
        isQueriesV2Enabled,
        baseInstructionSettings,
        baseEvaluationToolSettings,
        curriculumProgramType,
        learningGoalsFieldUid,
      }),
    [
      data,
      isQueriesV2Enabled,
      baseInstructionSettings,
      baseEvaluationToolSettings,
      curriculumProgramType,
      learningGoalsFieldUid,
    ]
  );

  const evaluationToolOptions = getEvaluationToolOptions(
    evaluationToolSettings,
    t
  );
  const instructionOptions = getInstructionOptions(instructionSettings, t);
  const otherItemOptions = getOtherItemOptions(answerBookletSettings, t);

  const [modalFields, setModalFields] = useState({
    instructions: [],
    evaluationTools: _.map(evaluationToolOptions, "value"),
    otherItems: [],
    questionLayout: { layoutValue: [TEXT_RESPONSE], settings: [] },
  });
  const [hasSettings, setHasSettings] = useState(false);

  const getPrintFileCall = useCallback(async () => {
    await getPrintFile({
      id: parentId,
      type: ASSESSMENT_TYPE_WORKSHEET,
      metadata: { modalFields, assignmentId, courseId },
      portalType,
    });
  }, [getPrintFile, parentId, modalFields, assignmentId, courseId, portalType]);

  const checkApplicableSettings = useCallback(async () => {
    const hasApplicableSettings = getHasApplicableSettings({
      instructions: instructionOptions,
      evaluationTools: evaluationToolOptions,
      otherItems: otherItemOptions,
      questionLayout: hasTextResponseBasedQuestion,
    });
    setHasSettings(hasApplicableSettings);

    if (!hasApplicableSettings) {
      setIsPDFDownloading(true);
      await getPrintFileCall();
      setIsPDFDownloading(false);
      toggleDownloadWorksheetModal();
    }
  }, [
    instructionOptions,
    evaluationToolOptions,
    otherItemOptions,
    hasTextResponseBasedQuestion,
    getPrintFileCall,
    toggleDownloadWorksheetModal,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (!_.isEmpty(selectedWorksheetDownloadModalSetting)) {
        const updatedModalFields = getUpdatedModalFieldsSettings({
          evaluationToolOptions,
          instructionOptions,
          otherItemOptions,
          selectedWorksheetDownloadModalSetting,
        });

        setModalFields(updatedModalFields);
      }
      checkApplicableSettings();
    }
  }, [isLoading]);

  const questionLayoutSettingsOptions = questionLayoutDefaultOptions();

  const updateModalFields = (params, key) => {
    const value = getTruthyKeysFromObject(params, key);

    setModalFields(prevState =>
      update(prevState, {
        [key]: { $set: value },
      })
    );
  };

  const updateQuestionLayoutAndSettings = (value, key) => {
    let updatedValue = value;
    if (_.isPlainObject(updatedValue)) {
      updatedValue = getTruthyKeysFromObject(updatedValue, key);
    }
    if (_.isEqual(updatedValue, [TEXT_RESPONSE])) {
      setModalFields(prevState =>
        update(prevState, {
          questionLayout: {
            settings: { $set: [] },
          },
        })
      );
    }
    setModalFields(prevState =>
      update(prevState, {
        questionLayout: {
          [key]: { $set: updatedValue },
        },
      })
    );
  };

  const saveWorksheetDownloadModalSetting = async () => {
    await updateSettings({
      settings: [
        {
          name: "PrintableWorksheetConfiguration",
          value: JSON.stringify(modalFields),
        },
      ],
      currentCurriculumProgramId: curriculumProgramId,
      queryVariablesSrc: CURRICULUM_LEVEL,
      mutationVariablesSrc: UPDATE_USER_CURRICULUM_SETTINGS,
      showToastMsg: false,
      portalType,
    });
  };

  const onDownloadButtonClick = async () => {
    setIsPDFDownloading(true);
    await saveWorksheetDownloadModalSetting();
    await getPrintFileCall();
    toggleDownloadWorksheetModal();
    setIsPDFDownloading(false);
  };

  const updateInstructions = value => updateModalFields(value, "instructions");

  const updateEvaluationTools = value =>
    updateModalFields(value, "evaluationTools");

  const updateOtherItems = value => updateModalFields(value, "otherItems");

  const updateQuestionLayout = value =>
    updateQuestionLayoutAndSettings(value, "layoutValue");

  const updateQuestionSettings = value =>
    updateQuestionLayoutAndSettings(value, "settings");

  const subTextClasses = ClassNames(classes.textSubtle, "text-body-2");

  const labelComponent = ({ informationText, labelLocale }) => (
    <QuestionFieldLabel
      informationText={informationText}
      labelLocale={labelLocale}
    />
  );
  return (
    <>
      {hasSettings ? (
        <Modal
          hasOverlay={true}
          height="min-content"
          width={960}
          onClose={toggleDownloadWorksheetModal}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEsc={true}
          testId="worksheet-downloadWorksheetModal-closeModal"
        >
          <Modal.Header
            isClosable={true}
            title={t("worksheet:print_layout_options")}
          ></Modal.Header>
          <Modal.Body isScrollable={true}>
            <div className={classes.container}>
              {hasTextResponseBasedQuestion ? (
                <div>
                  <UILabel
                    label={t("worksheet:questions_layout")}
                    labelContainerStyle={styles.labelContainerStyle}
                  />
                  <div className={classes.cardContainer}>
                    {_.map(
                      _.get(questionLayoutSettingsOptions, "layoutDetails"),
                      layoutDetail => {
                        const { title, value, subTitle, image } = layoutDetail;

                        return (
                          <CardSelection
                            type={"radio"}
                            key={value}
                            image={
                              <img
                                src={image}
                                className={classes.imageStyle}
                                alt=""
                              />
                            }
                            title={title}
                            value={value}
                            selectedValues={_.get(
                              modalFields,
                              "questionLayout.layoutValue"
                            )}
                            subtitle={
                              <div className={subTextClasses}>{subTitle}</div>
                            }
                            containerStyle={styles.containerStyle}
                            onCardSelectionClick={updateQuestionLayout}
                            testId={`worksheet-downloadWorksheetModal-${_.camelCase(
                              title
                            )}`}
                          />
                        );
                      }
                    )}
                  </div>
                  <div className={classes.settingContainer}>
                    <ToggleSwitchList
                      value={arrayKeysToTruthyObject(
                        _.get(modalFields, "questionLayout.settings")
                      )}
                      t={t}
                      options={_.get(questionLayoutSettingsOptions, "settings")}
                      toggleSwitchContainerStyle={styles.checkListContainer}
                      updateInputField={updateQuestionSettings}
                      fieldKey={"settings"}
                      isFromPrintSettings={true}
                      disabled={_.isEqual(
                        modalFields?.questionLayout?.layoutValue,
                        [TEXT_RESPONSE]
                      )}
                    />
                  </div>
                </div>
              ) : null}
              {!_.isEmpty(instructionOptions) ? (
                <div className={classes.settingContainer}>
                  <ToggleSwitchList
                    value={arrayKeysToTruthyObject(
                      _.get(modalFields, "instructions")
                    )}
                    t={t}
                    options={instructionOptions}
                    toggleSwitchContainerStyle={styles.checkListContainer}
                    updateInputField={updateInstructions}
                    labelComponent={labelComponent({
                      labelLocale: "classRoom:instructions",
                    })}
                    fieldKey={"instructions"}
                    isFromPrintSettings={true}
                  />
                </div>
              ) : null}
              {!_.isEmpty(evaluationToolOptions) ? (
                <div className={classes.settingContainer}>
                  <ToggleSwitchList
                    value={arrayKeysToTruthyObject(
                      _.get(modalFields, "evaluationTools")
                    )}
                    t={t}
                    options={evaluationToolOptions}
                    toggleSwitchContainerStyle={styles.checkListContainer}
                    updateInputField={updateEvaluationTools}
                    labelComponent={labelComponent({
                      informationText: "worksheet:evaluation_tools_subtext",
                      labelLocale: "worksheet:evaluation_tools",
                    })}
                    fieldKey={"evaluationTools"}
                    isFromPrintSettings={true}
                  />
                </div>
              ) : null}
              {!_.isEmpty(otherItemOptions) ? (
                <div className={classes.settingContainer}>
                  <ToggleSwitchList
                    value={arrayKeysToTruthyObject(
                      _.get(modalFields, "otherItems")
                    )}
                    t={t}
                    options={otherItemOptions}
                    toggleSwitchContainerStyle={styles.checkListContainer}
                    updateInputField={updateOtherItems}
                    labelComponent={labelComponent({
                      informationText: "worksheet:other_items_subtext",
                      labelLocale: "worksheet:answer_booklet",
                    })}
                    fieldKey={"otherItems"}
                    isFromPrintSettings={true}
                  />
                </div>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.modalFooterContainer}>
              <Button
                dsVersion="2.0"
                variant="progressive"
                size="large"
                _legacy_variant="progressive"
                _legacy_size="large"
                onClick={onDownloadButtonClick}
                testId="worksheet-downloadWorksheetModal-modalFooter-download"
              >
                {t("common:download")}
              </Button>
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="large"
                _legacy_size="large"
                _legacy_variant="outlined-subtle"
                onClick={toggleDownloadWorksheetModal}
                testId="worksheet-downloadWorksheetModal-modalFooter-cancel"
              >
                {t("common:cancel")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
      {isLoading || isPDFDownloading ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </>
  );
};
const mapStateToProps = (state, ownProps) => {
  const { parentId, parentType, portalType, templateId } = ownProps;
  const userId = getUserId();
  const userType = getUserType(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);

  const selectedWorksheetDownloadModalSetting = getSettingValue({
    name: "PrintableWorksheetConfiguration",
    userId,
    currentCurriculumProgramId: curriculumProgramId,
  });

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  // Create query variables for the enhancers
  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId,
      parentType,
      portalType,
    }
  );

  const assessmentQuestionsQueryVariablesV2 = {
    id: parentId,
    type: parentType,
    portalType,
  };

  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  // fetch assessment level details
  const assessmentDetails = getAssessmentDetailsFromCache({
    id: parentId,
    collaboratorsFilter,
  });
  const variables = getPlannerOrganizationResourcesOfNodeVariables({
    parentId,
    parentType,
  });
  const orgResources = getPlannerOrganizationResourcesOfNodeFromCache(
    variables
  );
  const assessmentTools = _.get(assessmentDetails, "assessmentTools.edges", []);
  const hasFinalRemarks = !_.isEmpty(
    _.find(
      assessmentTools,
      item => _.get(item, "node.assessmentToolType") === COMMENT
    )
  );

  const resources = _.get(orgResources, "organizationResourcesV2.edges", []);
  const hasResources = _.size(resources) > 0;
  const hasAssessmentTools =
    _.size(
      _.filter(assessmentTools, item => {
        const assessmentToolType = _.get(item, "node.assessmentToolType");
        return assessmentToolType !== COMMENT && assessmentToolType !== SCORE;
      })
    ) > 0;

  const worksheetTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [templateId] },
    false
  );

  const learningGoalsUid =
    _.get(worksheetTemplateCacheData, "body.step_list.pypElements.fields") ||
    _.get(worksheetTemplateCacheData, "body.step_list.dpElements.fields") ||
    [];

  const learningGoalsFieldUid = _.without(learningGoalsUid, "enableRating");

  const hasLearningGoals = getHasLearningGoals({
    assessmentDetails,
    learningGoalsFieldUid,
  });

  const isLearningGoalsEvaluationEnabled = _.get(
    assessmentDetails,
    "isLearningGoalsEvaluationEnabled",
    false
  );
  const hasAssessmentCriteria =
    hasLearningGoals && isLearningGoalsEvaluationEnabled;

  // Note: Question level details will be computed in component using enhancer data
  const voiceInstruction = _.find(_.get(assessmentDetails, "allFields", {}), {
    uid: FIELD_UID_VOICE_INSTRUCTIONS,
  });
  const voiceInstructionUrl = _.get(
    voiceInstruction,
    "resolvedMinimalTree.attachment.url",
    ""
  );

  // Base settings (not dependent on question data)
  const baseInstructionSettings = {
    [RESOURCES]: hasResources,
    [AUDIO_INSTRUCTIONS]: !_.isEmpty(voiceInstructionUrl),
  };

  const baseEvaluationToolSettings = {
    [FINAL_REMARKS]: hasFinalRemarks,
    [OVERALL_ASSESSMENT_TOOL]: hasAssessmentTools,
    [ASSESSMENT_CRITERIA]: hasAssessmentCriteria,
  };

  const courseId = _.get(state, "teacher.selected_class.selected_course");

  return {
    selectedWorksheetDownloadModalSetting,
    curriculumProgramId,
    courseId,
    baseInstructionSettings,
    baseEvaluationToolSettings,
    curriculumProgramType,
    learningGoalsFieldUid,
    assessmentQuestionsQueryVariables,
    assessmentQuestionsQueryVariablesV2,
    isQueriesV2Enabled,
    parentId,
    parentType,
    userType,
  };
};
const mapActionCreators = {
  updateSettings,
  getPrintFile,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getAssessmentQuestionsEnhancer(),
  getAssessmentQuestionsEnhancerV2()
)(DownloadWorksheetModal);

DownloadWorksheetModal.propTypes = {
  t: PropTypes.func,
  toggleDownloadWorksheetModal: PropTypes.func,
  selectedWorksheetDownloadModalSetting: PropTypes.object,
  curriculumProgramId: PropTypes.string,
  updateSettings: PropTypes.func,
  getPrintFile: PropTypes.func,
  parentId: PropTypes.string,
  assignmentId: PropTypes.string,
  portalType: PropTypes.string,
  courseId: PropTypes.string,
  baseInstructionSettings: PropTypes.object,
  baseEvaluationToolSettings: PropTypes.object,
  curriculumProgramType: PropTypes.string,
  learningGoalsFieldUid: PropTypes.array,
  isQueriesV2Enabled: PropTypes.bool,
};

DownloadWorksheetModal.defaultProps = {
  baseInstructionSettings: {},
  baseEvaluationToolSettings: {},
  curriculumProgramType: "",
  learningGoalsFieldUid: [],
  isQueriesV2Enabled: false,
  isLoading: false,
};
